<template>
  <section id="como-funciona" class="section-shell flow-section" data-theme="line">
    <div class="flow-header" data-reveal>
      <h2>Como funciona, na pr&aacute;tica.</h2>
    </div>

    <ol class="steps" data-reveal>
      <li v-for="(step, idx) in steps" :key="step">
        <span class="index"><b class="num">{{ idx + 1 }}</b></span>
        <span class="text" v-html="step"></span>
      </li>
    </ol>

    <p class="closing" data-reveal>
      <span class="closing-text">
        Tudo num lugar s&oacute;.<br />
        Sem ca&ccedil;ar informa&ccedil;&atilde;o em 15 abas abertas.
      </span>
    </p>
  </section>
</template>

<script setup>
const steps = [
  'Voc&ecirc; entra na <span class="word-brand">ZIMA</span>.',
  'Recebe o mapa do dia &mdash; <em>o que move o mercado agora.</em>',
  'Entende o porqu&ecirc; por tr&aacute;s do movimento.',
  'Executa com clareza.',
  'E sai sabendo mais do que entrou.',
];
</script>

<style scoped>
.flow-section {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif !important;
}

.flow-section,
.flow-section * {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif !important;
}

.flow-header {
  display: grid;
  gap: 4px;
}

.flow-header h2 {
  margin: 0;
  color: #eef3ff;
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  line-height: 1.12;
  letter-spacing: -0.02em;
}

.steps {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
  position: relative;
  display: grid;
  gap: 12px;
}

/* linha conectora — vertical no mobile */
.steps::before {
  content: '';
  position: absolute;
  left: 23px;
  top: 21px;
  bottom: 21px;
  width: 2px;
  background: linear-gradient(180deg, rgba(122, 162, 255, 0.55), rgba(189, 139, 255, 0.5));
  box-shadow: 0 0 12px rgba(115, 148, 240, 0.28);
}

.steps li {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* nó hexagonal — pontas laterais, a linha atravessa de ponta a ponta */
.index {
  position: relative;
  flex: 0 0 auto;
  width: 48px;
  height: 42px;
  display: grid;
  place-items: center;
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  background: linear-gradient(120deg, rgba(122, 162, 255, 0.95), rgba(189, 139, 255, 0.92));
  filter:
    drop-shadow(0 6px 14px rgba(91, 112, 210, 0.4))
    drop-shadow(0 0 8px rgba(115, 148, 240, 0.28));
  transition: transform 0.18s ease, filter 0.18s ease;
}

.index::before {
  content: '';
  position: absolute;
  inset: 1.5px;
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  background: linear-gradient(160deg, #141a30, #1a1530);
}

.index .num {
  position: relative;
  z-index: 1;
  font-weight: 800;
  font-style: normal;
  font-size: 1.15rem;
  color: #ffffff;
}

.steps li:hover .index {
  transform: translateY(-3px);
  filter:
    drop-shadow(0 10px 20px rgba(91, 112, 210, 0.5))
    drop-shadow(0 0 14px rgba(115, 148, 240, 0.42));
}

.text {
  color: #d5def2;
  font-size: clamp(1rem, 1.1vw, 1.14rem);
  line-height: 1.4;
  font-weight: 600;
}

/* placa de fechamento — pontas chanfradas, mesma linguagem facetada dos nós */
.closing {
  position: relative;
  display: inline-block;
  width: fit-content;
  margin: 24px 0 0;
  padding: 0;
  clip-path: polygon(14px 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0 50%);
  background: linear-gradient(120deg, rgba(122, 162, 255, 0.6), rgba(189, 139, 255, 0.55));
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.3));
}

.closing::before {
  content: '';
  position: absolute;
  inset: 1.5px;
  clip-path: polygon(13px 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 13px 100%, 0 50%);
  background:
    linear-gradient(90deg, rgba(78, 115, 223, 0.16), rgba(155, 89, 182, 0.08) 72%),
    linear-gradient(160deg, #10141f, #12101e);
}

.closing-text {
  position: relative;
  z-index: 1;
  display: block;
  padding: 13px 30px;
  color: #e8efff;
  font-size: clamp(1.02rem, 1.2vw, 1.28rem);
  line-height: 1.35;
  font-weight: 700;
  font-style: italic;
}

/* stepper horizontal no desktop */
@media (min-width: 900px) {
  .steps {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 14px;
  }

  .steps::before {
    left: 10%;
    right: 10%;
    top: 21px;
    bottom: auto;
    width: auto;
    height: 2px;
    background: linear-gradient(90deg, rgba(122, 162, 255, 0.55), rgba(189, 139, 255, 0.5));
  }

  .steps li {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 11px;
  }

  .text {
    max-width: 24ch;
  }

  .closing {
    display: block;
    margin: 26px auto 0;
  }

  .closing-text {
    text-align: center;
  }
}

@media (max-width: 899px) {
  .text {
    font-size: 1.02rem;
  }
}
</style>
